import React, { createContext, useContext } from 'react';
import { ReceiptPrinterService } from './ReceiptPrinterService';
import { getPrinterConfig } from '../../config/printer';
import { useClientConfig } from '../../common/client/ClientConfigProvider';
import { useClient } from '../../common/ClientProvider';

interface ReceiptPrinterServiceProviderProps {
  children: React.ReactNode;
  printerName?: string;
  clubName?: string;
}

const ReceiptPrinterServiceContext = createContext<ReceiptPrinterService | undefined>(undefined);

export const ReceiptPrinterServiceProvider: React.FC<ReceiptPrinterServiceProviderProps> = ({
  children,
  printerName,
  clubName
}) => {
  const { branding, locale } = useClientConfig();
  const { currentClientId } = useClient();
  // Fall back through prop → branding.clubName → currentClientId so receipts
  // never end up with a blank header when branding.clubName isn't seeded.
  const resolvedClubName = clubName || branding.clubName || currentClientId || '';
  const config = React.useMemo(
    () => getPrinterConfig(resolvedClubName),
    [resolvedClubName]
  );
  const service = React.useMemo(() => {
    return new ReceiptPrinterService(
      printerName || config.printerName,
      config.clubName,
      config.printerType as 'epson' | 'star',
      config.printerWidth,
      locale.currency,
      locale.timezone
    );
  }, [printerName, config, locale.currency, locale.timezone]);

  return (
    <ReceiptPrinterServiceContext.Provider value={service}>
      {children}
    </ReceiptPrinterServiceContext.Provider>
  );
};

export const useReceiptPrinterService = (): ReceiptPrinterService => {
  const context = useContext(ReceiptPrinterServiceContext);
  if (context === undefined) {
    throw new Error('useReceiptPrinterService must be used within a ReceiptPrinterServiceProvider');
  }
  return context;
}; 